// activity-tour-chrome — the "How this works" intro tour on the isolated editor surfaces
// (activity-intro-tours, Trello dxJDDAx5).
//
// Wired UNIFORMLY from main.js, not dispatched by data-surface, and EAGERLY — next to
// wireReadAloud / wireTimeOnTask, above the crossOriginIsolated early-return and outside
// loadProject().then(). It follows read-aloud-chrome.js for the same reason: its inputs are the
// SERVER-RENDERED <body> attributes and the surface's own `#*-config` JSON block, so it needs
// neither the project payload nor getEditor(). Late wiring would drop the control whenever
// loadProject() rejects — and the card's own manual test demands the control still be there when
// the payload fails to load. It is NOT a `SURFACES` entry: that map is per-surface-exclusive.
//
// It shares ONE engine with the classroom island: `src/islands/shared/activity-tour.js`. Both
// trees are entries of the same Vite `rollupOptions.input`, so the cross-tree import resolves at
// build time into a shared chunk named `activity-tour` (named by a PREFIX match in
// vite.config.mjs, because chunkFileNames routes it into the un-hashed js/shell/ directory).
//
// It never touches editor-core or editor-api, and it changes no postMessage contract.
//
// ── THE ALLOW-LIST IS LOAD-BEARING ──────────────────────────────────────────────────────────
// Wired uniformly, this module runs on ALL ELEVEN isolated surfaces — including
// `lib/views/embed/review.html` and `lib/views/embed/challenge-feedback.html`, which are TEACHER
// surfaces (classify by the route's auth pre-handler, never by the template name). Since
// activity-tour-remaining-types (Trello I0ebCnI2) shipped `assignment` and `challenge` copy,
// there is no accidental self-guard left: without SURFACE_TYPES both teacher surfaces would now
// auto-start student tours and write to teachers' records. The allow-list is the only thing
// standing there, and it must be extended DELIBERATELY, one surface at a time, never by omission.
//
// THE EXCLUSIONS ARE SPEC-PINNED, and `editor` matters most. It is the DEFAULT surface
// (`clientside-base.html:540` `{% block surface %}editor{% endblock %}`, with `main.js`'s own
// fallback to `'editor'`), so it covers the plain project editor and every standalone pgzero
// page, reachable by any signed-in owner — none of which is an activity at all. `review`,
// `challengeFeedback`, `viewonly`, `trace-capture` and `editor` are all asserted ABSENT by
// `test/lib/islands/activity-tour-surfaces.js`.
//
// ── AND THE ALLOW-LIST IS NOT ENOUGH ON ITS OWN ─────────────────────────────────────────────
// `predict.html` serves BOTH the student surface and the TEACHER CAPTURE surface from one
// template with the same `{% block surface %}predict{% endblock %}`, so `data-surface` does not
// separate them. The VARIANT does: `student` when `body[data-predict-student="true"]` (set only
// on the student branch by `project.js` `predictRun`, never by the capture handler), otherwise
// `capture`, which resolves to an empty step list — so no control renders and no tour can start.
//
// `data-is-owner` is explicitly NOT a staff proxy here: it is
// `request.user.id === project._creator` on a SYNTHESIZED project, so co-teachers and
// `manage-course-content` holders both read false.

import {
  startTour,
  endTour,
  tourSuppressed,
} from '../../../src/islands/shared/activity-tour.js';
import { resolveStepList } from '../../../src/islands/shared/activity-tour-steps.js';
import {
  readSeenAttribute,
  isTourableType,
  seenKeyFor,
  sendSeenList,
} from '../../../src/islands/shared/activity-tour-seen.js';

// The surfaces that get an intro tour, mapped to the `Material.type` whose copy they use.
// Written as a MAP rather than a list even though every current entry maps to its own name,
// because `data-surface` and `Material.type` are separate vocabularies that merely happen to
// agree here (`trace-capture` already does not) — a surface added to a bare list would silently
// resolve the wrong copy, or none.
//
// EIGHT SURFACES, and the map's MISS PATH (`if (!type) { return; }` below) is what makes every
// other isolated surface structurally unreachable. DO NOT ADD `review`, `challengeFeedback`,
// `viewonly`, `trace-capture` or — above all — `editor`; see the header.
//
// ADDING AN ENTRY HERE IS A SILENT NO-OP ON ITS OWN. `variantFor` must gain a branch for the
// same surface, or it returns `''`, which resolves an empty step list and renders nothing.
var SURFACE_TYPES = {
  predict: 'predict',
  assignment: 'assignment',
  challenge: 'challenge',
  debug: 'debug',
  tweak: 'tweak',
  completion: 'completion',
  'guided-make': 'guided-make',
  // find-the-bug-activity: the EIGHTH surface. Its `variantFor` branch below returns `student`
  // unconditionally whenever the config block exists — the authored spec and the linked program
  // are present from the first render, so there is no readiness rung beyond the gate anchor.
  'find-the-bug': 'find-the-bug',
};

// Inlined `sparkles`, the read-aloud-chrome.js / challenge-chrome.js precedent.
//
// INLINED ON PURPOSE — do NOT use <pc-icon> here. It upgrades on the isolated surfaces only as a
// SIDE EFFECT of pc-feedback.js, which sits inside `{% if user %}` AND inside an overridable
// `{% block feedback_widget %}` and loads as a separate module script racing this eager chrome.
// The control would paint icon-less or zero-width. The classroom, where <pc-icon> is a
// first-class import, uses <pc-icon>.
//
// The geometry and every wrapper attribute are copied VERBATIM from this repo's own icon set
// (`src/components/icon-names.ts` `sparkles`, rendered by `pc-icon.ts` at stroke-width 1.75), NOT
// hand-rolled from upstream Lucide: the whole claim of this control is ONE affordance that looks
// identical in the classroom header and here, and a differing glyph quietly undercuts it. If the
// icon set's `sparkles` ever changes, this copy has to change with it.
var SPARKLES_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M12 3 13.9 8.6 19.5 10.5 13.9 12.4 12 18 10.1 12.4 4.5 10.5 10.1 8.6Z"/>'
  + '<path d="M19 15l.9 2.5L22.5 18.5 20 19.4 19 22l-.9-2.6L15.5 18.5 18 17.5Z"/></svg>';

// One wiring per page load.
var wired = false;

/** The surface's own config block. Null rather than a throw on malformed JSON — a broken config
 *  must not take the whole eager wiring down. The `time-on-task-chrome.js` reading. */
function readConfig(doc, surface) {
  var el = doc.getElementById(surface + '-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '{}');
    return (cfg && typeof cfg === 'object') ? cfg : null;
  } catch (e) {
    return null;
  }
}

/**
 * The variant for this surface. `predict.html` renders BOTH the student surface and the teacher
 * capture surface, so this — not the allow-list — is what keeps a dead control off the capture
 * one. `capture` has no step list, so nothing renders and nothing can start.
 *
 * IT IS ALSO THE ISOLATED EQUIVALENT OF A CLASSROOM LADDER RUNG. Returning `''` resolves an empty
 * step list, which means no control and no tour — the right answer whenever the surface's own
 * config shows the activity has none of the elements its copy describes.
 *
 * EVERY RUNG BELOW TESTS NON-EMPTY, NEVER MERELY PRESENT, and that is load-bearing. THE EMPTY
 * ARRAY IS THE DANGEROUS CASE: `completion-chrome.js:92` coerces `cfg.blanks` to `[]` and renders
 * the code and the Check button anyway, so with `blanks: []` the gate OPENS, `#runner-console` is
 * server-rendered, and the list settles at 3 of 4 with `.completion-blank` dropped — a truncated
 * tour that reports `shown: true` and records `completion` seen FOR EVER. `tweak-chrome.js:35`
 * rejects only a NON-array, so `controls: []` renders `.tweak-code` and `#tweak-reset` but no
 * `.tweak-w` → 2 of 3, the same outcome. A `cfg.blanks === undefined` test would ship both.
 *
 * ACCEPTED RESIDUAL — NECESSARY BUT NOT SUFFICIENT. Two authoring-error shapes survive these
 * rungs and still truncate: `completion-chrome.js:93-97` drops any blank whose `line` is outside
 * `1..baseLines.length`, and `tweak-chrome.js:276-303` inserts `.tweak-w` only where a control's
 * constant was matched in the source. Closing either would mean re-implementing the chrome's own
 * matching here. Recorded; not claimed away.
 *
 * `assignment` and `challenge` need no rung — they publish no config block and all four of their
 * anchors are unconditional.
 */
function variantFor(doc, surface) {
  if (surface === 'predict') {
    return doc.body.getAttribute('data-predict-student') === 'true' ? 'student' : 'capture';
  }
  if (surface === 'assignment' || surface === 'challenge') { return 'student'; }

  var cfg = readConfig(doc, surface);
  if (surface === 'debug') {
    // `debug-chrome.js` bails outright with no config, so nothing its copy names is built.
    return cfg ? 'student' : '';
  }
  if (surface === 'tweak') {
    // `Array.isArray`, not `(cfg.controls || []).length`, so this test is EXACTLY
    // `tweak-chrome.js:35`'s. A non-array with a truthy `.length` (a string) would otherwise
    // resolve `student` here while the chrome bailed, leaving `.tweak-code` — the gate — never
    // built and the readiness poll re-running on every load for ever.
    return (cfg && Array.isArray(cfg.controls) && cfg.controls.length) ? 'student' : '';
  }
  if (surface === 'completion') {
    return (cfg && ((cfg.blanks || []).length)) ? 'student' : '';
  }
  if (surface === 'guided-make') {
    return (cfg && ((cfg.steps || []).length)) ? 'student' : '';
  }
  if (surface === 'find-the-bug') {
    // `find-the-bug-chrome.js` bails only when the config block is missing, like debug.
    return cfg ? 'student' : '';
  }
  return '';
}

function makeControl(doc) {
  var button = doc.createElement('button');
  button.type = 'button';
  // The engine owns this class's styling, so the control looks and behaves identically here and
  // in the classroom header, and the engine can find it for the auto-start focus-restore rule.
  button.className = 'pctour-replay';
  button.innerHTML = SPARKLES_SVG + '<span>How this works</span>';
  return button;
}

/**
 * EAGER entry point, wired from main.js above the crossOriginIsolated early-return and outside
 * loadProject().then(). Null-safe and throw-safe: it runs inside main.js's IIFE, where a throw
 * would skip every later wiring step.
 */
export function wireActivityTour(doc) {
  try {
    doc = doc || (typeof document !== 'undefined' ? document : null);
    if (!doc || !doc.body || wired) { return; }

    var surface = doc.body.getAttribute('data-surface') || 'editor';
    var type = SURFACE_TYPES[surface];
    if (!type) { return; }

    var variant = variantFor(doc, surface);
    // The structural gate. An empty list means no control and no tour — a teacher on the capture
    // surface gets neither, rather than a live button that resolves zero steps.
    if (!resolveStepList(type, variant).length) { return; }

    // THE SEEN KEY IS COMPUTED ONCE AND USED AT EVERY SITE — the validity test, the first-
    // encounter test and both halves of `record()` below. `seenKeyFor` is the ONE place the key
    // shape is decided (`activity-tour-seen.js`), mirroring the classroom's `_maybeStartTour`.
    //
    // Converting only the WRITE would leave the auto-start test comparing a different key the
    // moment a mode-sensitive surface exists here, which is precisely the trap this closes.
    // Behaviour is identical today: none of the seven allow-listed surfaces is in
    // `PER_VARIANT_SEEN`, so `seenKeyFor` returns the bare type for every one of them.
    var seenKey = seenKeyFor(type, variant);

    // ABSENT means anonymous — no control, no tour, no request. EMPTY means signed in having
    // seen nothing, which is every existing user. `(attr || '').split(',')` would collapse the
    // two, so `readSeenAttribute` tests `getAttribute(...) === null` first and returns null for
    // the anonymous case.
    var seen = readSeenAttribute(doc.body);
    if (seen === null) { return; }

    var cfg = readConfig(doc, surface);
    // PREVIEW HAS TWO SIGNALS, AND BOTH ARE NEEDED. A previewing teacher AUTO-STARTS (they
    // should see exactly what a student sees) but NEVER writes.
    //
    // The config block is the signal `time-on-task-chrome.js` reads, and it is the only one the
    // six surfaces that publish a `#<surface>-config` need. But `assignment` and `challenge`
    // publish NO config block — verified: only `completion`, `debug`, `guided-make`, `predict`,
    // `trace-capture` and `tweak` do. On those two `cfg === null`, so a config-only test reports
    // `preview === false` and `record()` writes `assignment` / `challenge` into the previewing
    // TEACHER's own `seenActivityIntros` — breaking this file's stated contract just above.
    //
    // `body[data-preview="true"]` is the signal that DOES exist on both, emitted under
    // `{% if isPreview %}` (`assignment.html:15`, `challenge.html:20`). Reading it is the whole
    // fix; adding config blocks to those templates would be a controller change this card does
    // not scope.
    var preview = !!(cfg && cfg.preview === true)
      || doc.body.getAttribute('data-preview') === 'true';

    // NO MOUNT, NO TOUR. Bailing here rather than carrying on is the point: proceeding would
    // auto-start a tour with no visible control anywhere on the surface, and leave
    // `restoreFocus` with no `.pctour-replay` to hand a keyboard user back to. Only a template
    // that forgot its `{% block toolbar %}` mount can reach this, so it is a wiring defect and it says so.
    var mount = doc.getElementById('activity-tour-mount');
    if (!mount) {
      console.error('activity-tour-chrome: no #activity-tour-mount on surface "' + surface + '"');
      return;
    }

    wired = true;

    var button = makeControl(doc);
    mount.appendChild(button);

    // The close-on-success countdown owns a capture-phase Escape handler, capture-phase
    // cancel-on-any-trusted-interaction listeners and takes focus after 450 ms — and
    // predict-chrome.js calls offerClose/offerReturn, so this surface is exactly where the two
    // would collide. The control is DISABLED, never hidden, while that prompt is VISIBLE.
    //
    // VISIBILITY, NEVER DOM PRESENCE: `close-prompt.js` inserts `.pccp-scrim` once and every hide
    // path only sets `hidden = true`. A presence check would permanently disable this control
    // for the rest of the page load once a student passed the activity, silently.
    // Assigned rather than declared: this sits inside `wireActivityTour`'s try/catch, so a
    // `function` statement here is a block-scoped inner declaration (`no-inner-declarations`).
    // The assignment precedes every use below, so hoisting semantics change nothing.
    var syncDisabled = function () {
      button.disabled = tourSuppressed(doc);
    };
    syncDisabled();
    // Cheap and self-correcting: the countdown appears and disappears without an event this
    // module can subscribe to, and the only cost of being a beat late is one press that is
    // refused by the engine anyway (it re-tests the same rule).
    var poll = window.setInterval(syncDisabled, 500);
    window.addEventListener('pagehide', function () {
      window.clearInterval(poll);
      endTour();
    });

    // Assigned, not declared, for the same reason as `syncDisabled` above — and likewise
    // defined before its only use (the auto-start `.then()` at the foot of this function).
    var record = function () {
      // A previewing teacher never writes; neither does a replay (this only runs from the
      // auto-start path below).
      if (preview) { return; }
      if (!isTourableType(seenKey)) { return; }
      if (seen.indexOf(seenKey) !== -1) { return; }
      seen.push(seenKey);
      // activity-tour-seen-once: POST ONLY THE KEY JUST RECORDED, never the whole list. `seen`
      // is a snapshot the server baked into `data-seen-intros` when THIS page rendered, and the
      // server write replaced the stored list with whatever it was sent — so posting the whole
      // array erased every key another tab recorded after this page loaded. The local
      // `seen.push` above stays (it is inert here, since `record()` is reachable only from the
      // single auto-start `.then()`, but it is pinned by a spec and is genuinely load-bearing in
      // the classroom consumer). The `isTourableType(seenKey)` guard above is now load-bearing
      // too, and MIND THE DIRECTION: an empty array still means CLEAR on the server, and
      // `sendSeenList` posts whatever survives `sanitiseSeenList`. So the danger is a key
      // sanitisation DROPS — that posts `[]` and wipes the student's whole history. The guard
      // is what keeps an unrecognised key from ever reaching the send at all.
      sendSeenList([seenKey]);
    };

    button.addEventListener('click', function () {
      // A REPLAY NEVER WRITES — no `record()` here, deliberately.
      void startTour({ type: type, variant: variant, doc: doc });
    });

    // First encounter only. `shown` is the engine's asynchronous answer to "was anything
    // actually displayed": a readiness deadline that expires renders nothing and reports false,
    // so the key stays unseen and a later visit still gets its tour.
    if (seen.indexOf(seenKey) === -1) {
      void startTour({ type: type, variant: variant, doc: doc }).then(function (result) {
        if (result && result.shown) { record(); }
      });
    }
  } catch (e) {
    console.error(e);
  }
}
